/**
 * Host-driven theme for the embedded results overlay.
 *
 * CSS custom properties cannot cross an iframe boundary, so the widget sends
 * the resolved `--es-*` tokens over postMessage and we map them onto the M3
 * palette here. Only `/embed` uses this; the standalone app is untouched.
 */

import { createTheme, type Theme } from '@mui/material/styles';
import { darkTheme, lightTheme } from './theme';

export type EmbedThemeMode = 'light' | 'dark';

export interface EmbedTokens {
  'color-primary'?: string;
  'color-on-primary'?: string;
  surface?: string;
  'on-surface'?: string;
  'surface-variant'?: string;
  'on-surface-variant'?: string;
  outline?: string;
  'header-bg'?: string;
  'header-fg'?: string;
  'font-family'?: string;
  radius?: string;
}

function parsePx(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function channelLuminance(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function parseHex(color: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!match) return null;
  const hex = match[1];
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
}

function relativeLuminance(color: string): number | null {
  const rgb = parseHex(color);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(channelLuminance);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const [lighter, darker] = la > lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}

/** Warn the integrator early rather than shipping unreadable text. */
function warnOnLowContrast(tokens: EmbedTokens): void {
  const pairs: Array<[string, string | undefined, string | undefined]> = [
    ['text on surface', tokens['on-surface'], tokens.surface],
    ['link on surface', tokens['color-primary'], tokens.surface],
    ['header text on header', tokens['header-fg'], tokens['header-bg']],
  ];

  for (const [label, foreground, background] of pairs) {
    if (!foreground || !background) continue;
    const ratio = contrastRatio(foreground, background);
    if (ratio !== null && ratio < 4.5) {
      console.warn(
        `[EnterpriseSearch] Low contrast for ${label}: ${ratio.toFixed(2)}:1 ` +
          `(${foreground} on ${background}). WCAG AA requires 4.5:1.`
      );
    }
  }
}

export function createEmbedTheme(tokens: EmbedTokens, mode: EmbedThemeMode): Theme {
  const base = mode === 'dark' ? darkTheme : lightTheme;
  if (Object.keys(tokens).length === 0) return base;

  warnOnLowContrast(tokens);

  const surface = tokens.surface ?? base.palette.background.default;
  const surfaceVariant = tokens['surface-variant'] ?? base.palette.surfaceContainer;
  const onSurface = tokens['on-surface'] ?? base.palette.text.primary;
  const onSurfaceVariant = tokens['on-surface-variant'] ?? base.palette.onSurfaceVariant;
  const outline = tokens.outline ?? base.palette.outline;
  const primary = tokens['color-primary'] ?? base.palette.primary.main;

  return createTheme(base, {
    palette: {
      primary: {
        main: primary,
        contrastText: tokens['color-on-primary'] ?? base.palette.primary.contrastText,
      },
      background: { default: surface, paper: surface },
      text: { primary: onSurface, secondary: onSurfaceVariant },
      divider: outline,
      outline,
      outlineVariant: outline,
      onSurfaceVariant,
      // One host token drives the whole container ladder; without colour maths
      // we keep the two ends distinct and let the middle share a value.
      surfaceContainerLowest: surface,
      surfaceContainerLow: surface,
      surfaceContainer: surfaceVariant,
      surfaceContainerHigh: surfaceVariant,
      surfaceContainerHighest: surfaceVariant,
      surfaceVariant,
    },
    typography: {
      fontFamily: tokens['font-family'] ?? base.typography.fontFamily,
    },
    shape: {
      borderRadius: parsePx(tokens.radius, base.shape.borderRadius as number),
    },
  });
}
